import { Box } from '@chakra-ui/layout'
import { useToast } from '@chakra-ui/react'
import { TableBuild } from 'components/organisms'
import { TableBuildProps } from 'components/organisms/TableBuild/interfaces'
import { useInstitutionsContext } from 'contexts/institutions'
import { motion } from 'framer-motion'
import {
    ForwardRefRenderFunction,
    forwardRef,
    memo,
    useEffect,
    useImperativeHandle,
    useState
} from 'react'
import { getFinanceReports } from 'services/report'
import formatter from 'utils/formatter'
import { removeEmptyValues } from 'utils/removeEmptyValues'
import TotalizerBox from '../../components/TotalizerBox'
import {
    headersAnalytical,
    headersSynthetic,
    keysFormatCurrency
} from '../../constants'
import {
    FinanceFilterDTO,
    FinancialAnalytical,
    FinancialElementsAnalytical,
    FinancialElementsSynthetic,
    FinancialSynthetic,
    TAnalysisViewMode
} from '../../types'
import { defaultLoader, getTotalizerBoxes } from './constants'

interface IPropsModelTable {
    filter: FinanceFilterDTO
    analysisViewMode: TAnalysisViewMode
}

interface IModeTableRef {
    resetPage: () => void
}

const ModeTableFC: ForwardRefRenderFunction<IModeTableRef, IPropsModelTable> = (
    { filter, analysisViewMode },
    refModeTable
) => {
    const toast = useToast()
    const [loading, setLoading] = useState(false)
    const [totalPages, setTotalPages] = useState(1)
    const [page, setPage] = useState<number>(1)
    const [limit, setLimit] = useState(10)
    const { currentInstitution, currentAgent } = useInstitutionsContext()
    const [totalizers, setTotalizers] = useState<any>()
    const isSynthetic = analysisViewMode === 'synthetic'

    useImperativeHandle(refModeTable, () => ({
        resetPage() {
            setInitialPage()
        }
    }))

    const [table, setTable] = useState<
        TableBuildProps<FinancialElementsSynthetic>
    >({
        ...headersSynthetic
    })

    const setInitialPage = () => setPage(1)

    const buildSyntheticTable = (listFinanceReports: FinancialSynthetic[]) => {
        return listFinanceReports.map(report => {
            const formattedReport = {
                ...report,
                ...Object.fromEntries(
                    keysFormatCurrency
                        .filter(
                            key =>
                                report[key as FinancialElementsSynthetic] !==
                                undefined
                        )
                        .map(key => [
                            key,
                            formatter.moeda(
                                report[key as FinancialElementsSynthetic]
                            )
                        ])
                ),
                cnpjAgente: formatter.cnpj(report.cnpjAgente)
            }

            return {
                ...formattedReport,
                totalValorRegistradora: (
                    <Box as="span" fontWeight="bold">
                        {formattedReport.totalValorRegistradora}
                    </Box>
                ),
                totalTaxaRegistro: (
                    <Box as="span" fontWeight="bold">
                        {formattedReport.totalTaxaRegistro}
                    </Box>
                )
            }
        })
    }

    const buildAnalyticalTable = (
        listFinanceReports: FinancialAnalytical[]
    ) => {
        return listFinanceReports.map(report => ({
            ...report,
            ...Object.fromEntries(
                keysFormatCurrency
                    .filter(
                        key =>
                            report[key as FinancialElementsAnalytical] !==
                            undefined
                    )
                    .map(key => [
                        key,
                        formatter.moeda(
                            report[key as FinancialElementsAnalytical]
                        )
                    ])
            ),
            dataRegistro: formatter.data(report.dataRegistro),
            cnpjAgente: formatter.cnpj(report.cnpjAgente)
        }))
    }

    const buildTable = (
        listFinanceReports: FinancialSynthetic[] | FinancialAnalytical[]
    ) => {
        return isSynthetic
            ? buildSyntheticTable(listFinanceReports as FinancialSynthetic[])
            : buildAnalyticalTable(listFinanceReports as FinancialAnalytical[])
    }

    const customizeHeaders = () => {
        if (currentInstitution?.id) {
            return isSynthetic
                ? headersSynthetic.header
                : headersAnalytical.header
        }

        return [
            {
                name: 'razaoSocialInstituicaoFinanceira',
                text: 'Instituição Financeira',
                position: 0
            },
            ...(isSynthetic
                ? headersSynthetic.header
                : headersAnalytical.header)
        ]
    }

    const buildPayload = () => {
        const payload = removeEmptyValues({
            pagina: page,
            limite: limit,
            ...filter,
            agrupar: isSynthetic,
            cnpjAgente: currentAgent?.cnpj
        })

        return payload
    }

    async function fetchListFinanceReports() {
        setLoading(true)

        try {
            const response = await getFinanceReports(
                buildPayload(),
                currentInstitution?.id
            )

            const {
                sinteticoPorTipoServico,
                taxaRegistroTotal,
                valorRegistradoraTotal,
                quantidadeTotal
            } = response.data

            setTotalPages(+response.headers['total-pages'])

            setTotalizers({
                ...sinteticoPorTipoServico,
                taxaRegistroTotal,
                valorRegistradoraTotal,
                quantidadeTotal
            })

            setTable((old: any) => ({
                ...old,
                header: customizeHeaders(),
                body: buildTable(response.data.operacoes)
            }))
        } catch (error) {
            toast({
                title: 'Sentimos muito!',
                description: 'Ocorreu algum erro, tente novamente mais tarde.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        setLoading(true)

        if (currentInstitution?.id !== undefined) {
            fetchListFinanceReports()
        }
    }, [page, limit, currentInstitution?.id, filter, currentAgent?.cnpj])

    const handlePage = (value: number) => {
        if (page !== value) {
            setPage(value)
            setLoading(true)
        }
    }

    const handleLimit = (value: number) => {
        setLimit(value)
        setPage(1)
        setLoading(true)
    }

    return (
        <>
            <Box my="24px" display="flex" gap="8px">
                {getTotalizerBoxes(totalizers).map((box, index) => (
                    <TotalizerBox
                        key={index}
                        isLoading={loading}
                        {...box}
                    />
                ))}
            </Box>

            <Box
                as={motion.div}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                <TableBuild
                    header={table.header}
                    body={table.body}
                    action={table.action}
                    loading={{
                        status: loading,
                        loader: defaultLoader
                    }}
                    pagination={{
                        page,
                        limit,
                        totalPages
                    }}
                    onChangePage={handlePage}
                    onChangeLimit={handleLimit}
                />
            </Box>
        </>
    )
}

export const ModeTable = memo(forwardRef(ModeTableFC))